import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "panel", "toggle", "segment", "status", "retry"]
  static values = {
    delay: { type: Number, default: 800 },
    defaultSort: { type: String, default: "raised" },
    defaultSegment: { type: String, default: "all" }
  }

  connect() {
    this.restoreCachedResults()
    this.formTarget.querySelectorAll('details[data-filter-name="city"]').forEach(details => this.filterCityChoices(details))
    this.timer = null
    this.formRevision = 0
    this.submittedRevision = null
    this.activeSubmission = null
    this.formDataHandler = (event) => this.compact(event.formData)
    this.formTarget.addEventListener("formdata", this.formDataHandler)
    this.dismissChoices = (event) => {
      this.formTarget.querySelectorAll("details[data-filter-name][open]").forEach(details => {
        if (!details.contains(event.target)) details.open = false
      })
    }
    document.addEventListener("click", this.dismissChoices)
    this.failedResponseHandler = (event) => {
      // An infrastructure error page has no matching Turbo frame. Keep the
      // editable filters so submit-end can offer Retry instead of "Content missing".
      if (event.detail.fetchResponse.statusCode >= 500) event.preventDefault()
    }
    this.formTarget.addEventListener("turbo:before-fetch-response", this.failedResponseHandler)
    this.frame = this.element.closest("turbo-frame")
    // Keep the user's open/closed choice while live filtering replaces this
    // workspace, but start collapsed on a fresh page visit.
    this.beforeFrameRender = (event) => {
      if (event.target !== this.frame) return

      // A response can arrive after the admin has resumed typing. Rendering
      // that stale response would replace the focused field and discard the
      // newer text, so leave the current frame in place for the queued search.
      if (this.submittedRevision !== null && this.formRevision !== this.submittedRevision) {
        // Do not pause Turbo's renderer with preventDefault(): without resume()
        // that leaves the frame's render queue blocked for every future filter.
        // Complete this obsolete render without replacing the newer inputs.
        event.detail.render = () => {}
        return
      }

      this.submittedRevision = null
      this.activeSubmission = null

      const panel = event.detail.newFrame.querySelector('[data-admin-marketing-filters-target="panel"]')
      const toggle = event.detail.newFrame.querySelector('[data-admin-marketing-filters-target="toggle"]')
      if (panel && toggle) this.syncPanel(panel, toggle, this.panelTarget.hidden)

      const focused = document.activeElement
      const citySearch = this.formTarget.querySelector("[data-city-search]")
      const nextCitySearch = event.detail.newFrame.querySelector("[data-city-search]")
      if (citySearch && nextCitySearch) nextCitySearch.setAttribute("value", citySearch.value)
      this.formTarget.querySelectorAll("details[data-filter-name][open]").forEach(details => {
        const replacement = event.detail.newFrame.querySelector(`details[data-filter-name="${details.dataset.filterName}"]`)
        if (replacement) replacement.open = true
      })
      if (this.formTarget.contains(focused) && focused.matches('input[type="checkbox"]')) {
        const name = focused.name, value = focused.value
        const render = event.detail.render
        event.detail.render = async (...args) => {
          await render(...args)
          const input = [...this.frame.querySelectorAll('input[type="checkbox"]')].find(input => input.name === name && input.value === value)
          input?.focus({ preventScroll: true })
        }
      }
      if (this.formTarget.contains(focused) && focused.matches('input[type="text"], input[type="search"]')) {
        const name = focused.name, start = focused.selectionStart, end = focused.selectionEnd
        const selector = focused.hasAttribute("data-city-search") ? "[data-city-search]" : `[name="${name}"]`
        const render = event.detail.render
        event.detail.render = async (...args) => {
          await render(...args)
          const input = this.frame.querySelector(selector)
          input?.focus({ preventScroll: true })
          input?.setSelectionRange(start, end)
        }
      }
    }
    this.frame?.addEventListener("turbo:before-frame-render", this.beforeFrameRender)
    this.syncPanel(this.panelTarget, this.toggleTarget, this.panelTarget.hidden)
  }

  disconnect() {
    window.clearTimeout(this.timer)
    this.formTarget.removeEventListener("formdata", this.formDataHandler)
    document.removeEventListener("click", this.dismissChoices)
    this.formTarget.removeEventListener("turbo:before-fetch-response", this.failedResponseHandler)
    this.frame?.removeEventListener("turbo:before-frame-render", this.beforeFrameRender)
  }

  queue(event) {
    if (event.isComposing) return
    this.formRevision += 1
    this.pending()
    window.clearTimeout(this.timer)
    this.timer = window.setTimeout(() => this.formTarget.requestSubmit(), this.delayValue)
  }

  filter() {
    this.formRevision += 1
    this.submit()
  }

  choose(event) {
    this.updateChoices(event.target.closest("details"))
    this.queue(event)
  }

  clearChoices(event) {
    const details = event.target.closest("details")
    details.querySelectorAll('input[type="checkbox"]').forEach(input => { input.checked = false })
    const search = details.querySelector("[data-city-search]")
    if (search) {
      search.value = ""
      this.filterCityChoices(details)
    }
    this.updateChoices(details)
    this.filter()
  }

  updateChoices(details) {
    const labels = [...details.querySelectorAll('input:checked')].map(input => input.dataset.choiceLabel)
    details.querySelector('[data-choice-summary]').textContent = labels.join(", ") || details.dataset.defaultLabel
  }

  searchCities(event) {
    this.filterCityChoices(event.target.closest("details"))
  }

  filterCityChoices(details) {
    const value = details.querySelector("[data-city-search]").value.trim().replace(/\s+/g, " ")
    const query = value.toLocaleLowerCase()
    let exact = false
    details.querySelectorAll('[data-choice-options] input[type="checkbox"]').forEach(input => {
      const label = input.dataset.choiceLabel.toLocaleLowerCase()
      if (label === query) exact = true
      input.closest("label").hidden = !input.checked && !label.includes(query)
    })
    const add = details.querySelector("[data-add-city]")
    add.hidden = !query || exact
    add.textContent = value ? `Add “${value}”` : "Add city"
  }

  addCity(event) {
    if (event.isComposing) return
    const details = event.target.closest("details")
    const search = details.querySelector("[data-city-search]")
    const value = search.value.trim().replace(/\s+/g, " ")
    if (!value) return
    let input = [...details.querySelectorAll('input[type="checkbox"]')]
      .find(input => input.value.toLocaleLowerCase() === value.toLocaleLowerCase())
    if (!input) {
      const label = document.createElement("label")
      input = document.createElement("input")
      input.type = "checkbox"
      input.name = "city[]"
      input.value = value
      input.dataset.choiceLabel = value
      input.dataset.action = "change->admin-marketing-filters#choose"
      const text = document.createElement("span")
      text.textContent = value
      label.append(input, text)
      details.querySelector("[data-choice-options]").append(label)
    }
    input.checked = true
    search.value = ""
    this.filterCityChoices(details)
    this.updateChoices(details)
    this.filter()
  }

  closeChoices(event) {
    event.currentTarget.open = false
    event.currentTarget.querySelector("summary").focus()
  }

  submit(event) {
    if (event) this.formRevision += 1
    window.clearTimeout(this.timer)
    this.pending()
    this.formTarget.requestSubmit()
  }

  toggle() {
    this.syncPanel(this.panelTarget, this.toggleTarget, !this.panelTarget.hidden)
  }

  syncPanel(panel, toggle, hidden) {
    panel.hidden = hidden
    toggle.classList.toggle("is-collapsed", hidden)
    toggle.setAttribute("aria-expanded", String(!hidden))
  }

  start(event) {
    this.activeSubmission = event.detail.formSubmission
    this.submittedRevision = this.formRevision
    this.element.classList.add("is-searching")
  }

  finish(event) {
    if (event.detail.formSubmission !== this.activeSubmission) return
    if (!event.detail.success) {
      this.submittedRevision = null
      this.activeSubmission = null
      this.element.classList.remove("is-searching")
      if (this.hasStatusTarget) this.statusTarget.textContent = "Results could not be updated. Retry before selecting contacts."
      if (this.hasRetryTarget) this.retryTarget.hidden = false
    }
  }

  pending() {
    this.element.classList.add("is-searching")
    const page = this.element.closest(".admin-marketing-page")
    if (page) {
      page.dataset.filtersPending = "true"
      page.querySelectorAll(".admin-marketing-results-card, .admin-marketing-header > button").forEach(node => { node.inert = true })
      page.querySelector(".admin-marketing-stat-grid")?.setAttribute("aria-busy", "true")
    }
    if (this.hasStatusTarget) this.statusTarget.textContent = "Updating results…"
    if (this.hasRetryTarget) this.retryTarget.hidden = true
  }

  restoreCachedResults() {
    const page = this.element.closest(".admin-marketing-page")
    if (page?.dataset.filtersPending !== "true") return
    // Back can restore an in-progress snapshot. Restore its server-rendered
    // filters (the input defaults) to match the cached results, not unsent edits.
    this.formTarget.reset()
    this.formTarget.querySelectorAll("details[data-filter-name]").forEach(details => this.updateChoices(details))
    delete page.dataset.filtersPending
    page.querySelectorAll(".admin-marketing-results-card, .admin-marketing-header > button").forEach(node => { node.inert = false })
    page.querySelector(".admin-marketing-stat-grid")?.removeAttribute("aria-busy")
    this.element.classList.remove("is-searching")
    if (this.hasStatusTarget) this.statusTarget.textContent = ""
    if (this.hasRetryTarget) this.retryTarget.hidden = true
  }

  compact(formData) {
    for (const [key, value] of [...formData.entries()]) {
      const text = String(value).trim()
      const isDefaultSort = key === "sort" && text === this.defaultSortValue
      const isDefaultSegment = key === "segment" && text === this.defaultSegmentValue && !formData.has("saved_list_edit")
      // "Any" is an explicit override now that Last contacted defaults to Never.
      const isDefaultContact = key === "contacted" && text === "never"
      if (text === "" || (text === "any" && key !== "contacted") || isDefaultContact || isDefaultSort || isDefaultSegment) formData.delete(key)
    }

    // Turbo updates history when the response arrives. Do not make the address
    // bar claim newer filters have loaded while the old audience is still shown.
  }
}
